import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';

interface DeleteSubmittedConfirmModalProps {
    reason: string;
    onReasonChange: (value: string) => void;
    isDeleting: boolean;
    error: string | null;
    onDismissError: () => void;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * "Delete this Dx report?" confirmation for submitted (non-draft) events.
 * Captures an optional free-form reason that is persisted on the
 * timeline's invalidation record.
 */
export function DeleteSubmittedConfirmModal({
    reason,
    onReasonChange,
    isDeleting,
    error,
    onDismissError,
    onCancel,
    onConfirm
}: DeleteSubmittedConfirmModalProps) {
    useEscapeKey(onCancel, !isDeleting);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Delete this Dx report?</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    This will hide the report from the patient&apos;s file list. The record will
                    remain in the internal timeline marked as deleted.
                </p>
                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Reason (optional)
                    </label>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => onReasonChange(e.target.value)}
                        placeholder="Enter a reason..."
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    />
                </div>
                {error && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        <span>{error}</span>
                        <button
                            onClick={onDismissError}
                            className="text-red-500 hover:text-red-700"
                            aria-label="Dismiss error"
                        >
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDeleting && (
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        )}
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}
